import { useEffect } from 'react';

interface PageMetaProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  publishedTime?: string;
  modifiedTime?: string;
}

export default function PageMeta({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  ogType = 'website',
  publishedTime,
  modifiedTime,
}: PageMetaProps) {
  useEffect(() => {
    const fullTitle = `${title} - 奶水乐园`;
    document.title = fullTitle;

    const setMeta = (name: string, content: string, attrName = 'name') => {
      let el = document.querySelector(`meta[${attrName}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attrName, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    setMeta('keywords', keywords);

    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = canonicalUrl;
    }

    setMeta('og:title', fullTitle, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:type', ogType, 'property');
    if (canonicalUrl) setMeta('og:url', canonicalUrl, 'property');
    if (ogImage) setMeta('og:image', ogImage, 'property');
    setMeta('og:site_name', '奶水乐园', 'property');
    setMeta('og:locale', 'zh_CN', 'property');

    setMeta('twitter:card', 'summary_large_image', 'name');
    setMeta('twitter:title', fullTitle, 'name');
    setMeta('twitter:description', description, 'name');
    if (ogImage) setMeta('twitter:image', ogImage, 'name');

    if (publishedTime) setMeta('article:published_time', publishedTime, 'property');
    if (modifiedTime) setMeta('article:modified_time', modifiedTime, 'property');

    return () => {
      // Clean up OG and Twitter tags on unmount
      ['og:title', 'og:description', 'og:type', 'og:url', 'og:image', 'og:site_name', 'og:locale',
        'twitter:card', 'twitter:title', 'twitter:description', 'twitter:image',
        'article:published_time', 'article:modified_time'].forEach((name) => {
        const el = document.querySelector(`meta[property="${name}"], meta[name="${name}"]`);
        if (el) el.remove();
      });
    };
  }, [title, description, keywords, canonicalUrl, ogImage, ogType, publishedTime, modifiedTime]);

  return null;
}